import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import type { BsdsInputEvent } from '../types/componentType';

export type MaskedBsdsInputConfig = {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  required?: boolean;
  format: (raw: string) => string;
  clean: (raw: string) => string;
  isComplete: (cleaned: string) => boolean;
  validate: (value: string) => boolean;
  invalidMessage: (cleaned: string) => string;
  requiredMessage: string;
  /** Optional transform applied when rendering the controlled value. */
  toDisplayValue?: (value: string) => string;
};

/** Normalize BsdsInput / native event payloads to a plain string. */
export function extractBsdsInputString(raw: unknown): string {
  if (raw === undefined || raw === null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return '';
  return String(raw);
}

function readEventValue(event: FormEvent<HTMLBsdsInputElement>): unknown {
  const target = event.target as HTMLBsdsInputElement;
  return (event as BsdsInputEvent).detail?.value ?? target?.value;
}

/**
 * Shared state/handlers for CPF, CNPJ and DocumentNumber Bsds inputs
 * (format on input, touched/invalid classes, complete-value validation).
 */
export function useMaskedBsdsInput({
  value,
  onChange,
  onValidationChange,
  required = false,
  format,
  clean,
  isComplete,
  validate,
  invalidMessage,
  requiredMessage,
  toDisplayValue,
}: MaskedBsdsInputConfig) {
  const [isValid, setIsValid] = useState(true);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const inputRef = useRef<HTMLBsdsInputElement>(null);

  const stringValue = typeof value === 'string' ? value : String(value || '');
  const cleaned = clean(stringValue);

  const errorMessage = (() => {
    if (hasBeenTouched && isComplete(cleaned) && !isValid) {
      return invalidMessage(cleaned);
    }
    if (hasBeenTouched && required && cleaned.length === 0) {
      return requiredMessage;
    }
    return '';
  })();

  const updateValidationClasses = useCallback(() => {
    const element = inputRef.current;
    if (!element) return;

    element.classList.toggle('bsds-touched', hasBeenTouched);
    element.classList.toggle('bsds-invalid', Boolean(hasBeenTouched && !isValid && errorMessage));
  }, [hasBeenTouched, isValid, errorMessage]);

  const applyFormattedValue = useCallback(
    (raw: unknown) => {
      onChange(format(extractBsdsInputString(raw)));
      setHasBeenTouched(true);
    },
    [format, onChange],
  );

  const handleChange = useCallback(
    (event: FormEvent<HTMLBsdsInputElement>) => {
      applyFormattedValue(readEventValue(event));
    },
    [applyFormattedValue],
  );

  const handleInput = useCallback(
    (event: FormEvent<HTMLBsdsInputElement>) => {
      applyFormattedValue(readEventValue(event));
    },
    [applyFormattedValue],
  );

  useEffect(() => {
    if (isComplete(cleaned)) {
      const valid = validate(stringValue);
      setIsValid(valid);
      onValidationChange?.(valid);
      return;
    }
    if (cleaned.length === 0) {
      const valid = !required;
      setIsValid(valid);
      onValidationChange?.(valid);
      return;
    }
    setIsValid(true);
    onValidationChange?.(false);
  }, [cleaned, stringValue, isComplete, validate, onValidationChange, required]);

  useEffect(() => {
    updateValidationClasses();
  }, [updateValidationClasses]);

  const displaySource = toDisplayValue ? toDisplayValue(stringValue) : stringValue;

  return {
    inputRef,
    errorMessage,
    safeValue: displaySource,
    handleChange,
    handleInput,
  };
}
